import React, { useState, useEffect } from 'react'

import {
  Box,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Typography,
} from '@material-ui/core'

import useFetch from 'hooks/useFetch'
import useFilter from 'hooks/useFilter'

import { FilterButton } from 'components'

import TableDataProcessesSelector from './TableDataProcessesSelector'
import FilterDataProcessesSelector from './FilterDataProcessesSelector'

import * as service from 'service'

const DEFAULT_PAGE_ROWS = 8

const DataProcessSelector = ({ useProvider, resourceOrigin }) => {
  const {
    selectedProcesses,
    setSelectedProcesses,
    toLinkProcesses,
    setToLinkProcesses,
    mountSelectAllProcesses,
    mountUnselectAllProcesses,
    loading,
  } = useProvider()

  const [page, setPage] = useState(1)
  const selectedProcessesIds = selectedProcesses.map(
    (selectedProcess) => selectedProcess.id,
  )

  const filter = useFilter()

  const { response, isLoading } = useFetch(
    service.dponet.dataProcesses.get,
    { perPage: 10000000, ...filter.filters },

    [filter.filters],
  )

  useEffect(() => {
    setPage(1)
  }, [isLoading])

  const unselect = (process) => {
    const newSelected = selectedProcesses.filter(
      (selectedProcess) => Number(selectedProcess.id) !== Number(process.id),
    )
    setSelectedProcesses(newSelected)
    const newToLink = toLinkProcesses.filter(
      (linkedProcess) => Number(linkedProcess) !== Number(process.id),
    )
    setToLinkProcesses(newToLink)
  }

  const selectProcess = (process, isSelected) => {
    if (isSelected) {
      if (checkIfIsNotSelected(process)) {
        const newSelected = [...selectedProcesses, process]
        setSelectedProcesses(newSelected)
        const newToLink = [...toLinkProcesses, process.id]
        setToLinkProcesses(newToLink)
      }
    } else {
      unselect(process)
    }
  }

  const checkIfIsNotSelected = (process) => {
    return !selectedProcessesIds.includes(process?.id)
  }

  const selectAllProcesses = (processes, isChecked, indeterminate) => {
    const processesCount = processes?.rowIds?.length

    if (processesCount === undefined) {
      return
    }

    const rows = processes?.rowIds?.map((rowId) => Number(rowId)) || []
    const start = (page - 1) * DEFAULT_PAGE_ROWS
    const end = start + DEFAULT_PAGE_ROWS

    const rowsIds = rows.slice(start, end)

    if (isChecked && !indeterminate) {
      if (processesCount !== mountDataProcessToSelect().length) {
        return
      }
      selectAll(rowsIds)
    } else {
      unselectAll(rowsIds)
    }
  }

  const selectAll = (rowsIds) => {
    const selectedIncoming = mountSelectAllProcesses(
      rowsIds.filter((id) => !selectedProcessesIds.includes(id)),
      [...response?.data.dataProcesses],
    )

    const newSelected = [...selectedIncoming, ...selectedProcesses]

    setSelectedProcesses(newSelected)
    const newToLink = [...rowsIds, ...toLinkProcesses]
    setToLinkProcesses([...new Set(newToLink)])
  }

  const unselectAll = (rowsIds) => {
    const newSelectedList = selectedProcesses
      .map((process) => process?.id)
      .filter((processId) => rowsIds.includes(processId))

    const newSelected = mountUnselectAllProcesses(newSelectedList, [
      ...selectedProcesses,
    ])
    setSelectedProcesses(newSelected)
    const newToLink = toLinkProcesses.filter(
      (rowId) => !rowsIds.includes(rowId),
    )

    setToLinkProcesses([...new Set(newToLink)])
  }

  const mountDataProcessToSelect = () => {
    return response?.data?.dataProcesses
      ?.filter(
        (dataProcess) =>
          !resourceOrigin?.dataProcesses
            ?.map((incidentDataProcess) => incidentDataProcess.id)
            .includes(dataProcess.id),
      )
      ?.map((process) => ({
        ...process,
        department: process?.department?.name,
      }))
  }

  const mountDefaultSelected = () => {
    const selected = {}
    toLinkProcesses.map((toLinkProcess) => (selected[toLinkProcess] = true))
    return selected
  }

  return (
    <>
      <Box pl={2}>
        <FilterButton setDrawerOpen={filter.setDrawerOpen}></FilterButton>
      </Box>
      <Box width="100%" height="70vh" display="flex">
        <Grid container>
          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            {isLoading ? (
              <Box
                display="flex"
                width="100%"
                minHeight="200px"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box p={2}>
                <Card>
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="h2">
                      Processos não afetados
                    </Typography>
                    <TableDataProcessesSelector
                      loading={isLoading}
                      page={page}
                      setPage={setPage}
                      defaultSelected={mountDefaultSelected()}
                      dataProcesses={mountDataProcessToSelect()}
                      onRowSelected={selectProcess}
                      onSelectionChange={selectAllProcesses}
                      perPage={DEFAULT_PAGE_ROWS}
                      checkboxSelection
                    />
                  </CardContent>
                </Card>
              </Box>
            )}
          </Grid>
          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <Box p={2}>
              <Card>
                <CardContent>
                  <Typography gutterBottom variant="h5" component="h2">
                    Processos afetados
                  </Typography>
                  <TableDataProcessesSelector
                    dataProcesses={loading ? [] : selectedProcesses}
                    perPage={DEFAULT_PAGE_ROWS}
                    loading={loading}
                  />
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
        <FilterDataProcessesSelector filter={filter} />
      </Box>
    </>
  )
}

export default DataProcessSelector
